import type { ApiTransport } from './client';

// ----------------------------------------------------------------------

type ClientOwnedKeys = 'url' | 'method' | 'params' | 'paramsSerializer' | 'data' | 'responseType';

/** Axios config a caller may add to one request; the client owns the method, the URL, the body and the response type. */
export type AxiosApiOptions<Config> = Omit<Config, ClientOwnedKeys>;

/**
 * Sends through the app's own axios instance, so its interceptors (auth headers, token refresh) apply. Generic over the
 * instance's request config, so each app keeps its own axios version and its own option types.
 */
export function createAxiosTransport<Config extends object>(
  getInstance: () => { request(config: Config): Promise<{ status: number; data: unknown }> }
): ApiTransport<AxiosApiOptions<Config>> {
  return async ({ method, url, body, responseType, options }) => {
    const { headers, ...rest } = (options ?? {}) as { headers?: object };

    // The apps' instances default to a JSON content type, which makes axios serialize FormData as JSON.
    const config = {
      ...rest,
      method,
      url,
      data: body,
      responseType,
      headers:
        body instanceof FormData ? { ...headers, 'Content-Type': 'multipart/form-data' } : headers,
    } as unknown as Config;

    const response = await getInstance().request(config);
    return response.status === 204 ? null : response.data;
  };
}
